import { useMemo, useRef, useState } from "react";
import { useOnClickOutside } from "../../customHooks/useOnClickOutside";

interface Props {
    label: string;
    id: string;
    value: string[];
    dataTextField: string;
    dataValueField: string;
    handleChange: (selectedValues: string[]) => void;
    rows: any[];
    placeholder?: string;
    searchPlaceholder?: string;
    customLeftCols?: any; // for example col-sm-3
    customRightCols?: any; // for example col-sm-9
}

const MultiSelectDropdown = (props: Props) => {

    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const containerRef = useRef<HTMLDivElement>(null);

    useOnClickOutside(containerRef, () => setOpen(false));

    const leftColumnClass = `${props.customLeftCols ?? "col-sm-3"} col-form-label zvlabel`;
    const rightColumnClass = `${props.customRightCols ?? "col-sm-9"}`;

    const rows = props.rows ?? [];

    const selectedRows = useMemo(() => {
        const selectedSet = new Set(props.value);
        return rows.filter((row: any) =>
            selectedSet.has(String(row[props.dataValueField]))
        );
    }, [rows, props.value, props.dataValueField]);

    const filteredRows = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) {
            return rows;
        }
        return rows.filter((row: any) =>
            String(row[props.dataTextField] ?? "").toLowerCase().indexOf(term) > -1
        );
    }, [rows, search, props.dataTextField]);

    const toggleValue = (valueAsString: string, checked: boolean) => {
        const newValues = checked
            ? [...props.value, valueAsString]
            : props.value.filter((id) => id !== valueAsString);
        props.handleChange(newValues);
    };

    return (
        <div className="row">
            {props.label && (
                <label htmlFor={props.id} className={leftColumnClass}>
                    {props.label}
                </label>
            )}
            <div className={rightColumnClass}>
                <div ref={containerRef} style={{ position: "relative" }}>
                    <div
                        id={props.id}
                        onClick={() => setOpen((prev) => !prev)}
                        className="form-select"
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "4px",
                            alignItems: "center",
                            minHeight: "38px",
                            height: "auto",
                            cursor: "pointer",
                        }}
                    >
                        {selectedRows.length === 0 && (
                            <span style={{ color: "#6c757d" }}>
                                {props.placeholder ?? ""}
                            </span>
                        )}
                        {selectedRows.map((row: any) => {
                            const valueAsString = String(row[props.dataValueField]);
                            return (
                                <span
                                    key={valueAsString}
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        backgroundColor: "#e9ecef",
                                        borderRadius: "12px",
                                        padding: "1px 8px",
                                        fontSize: "0.85rem",
                                        lineHeight: "1.4",
                                    }}
                                >
                                    {row[props.dataTextField]}
                                    <span
                                        role="button"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            toggleValue(valueAsString, false);
                                        }}
                                        style={{
                                            cursor: "pointer",
                                            fontWeight: "bold",
                                            lineHeight: "1",
                                        }}
                                    >
                                        &times;
                                    </span>
                                </span>
                            );
                        })}
                    </div>

                    {open && (
                        <div
                            style={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                right: 0,
                                zIndex: 1000,
                                backgroundColor: "#fff",
                                border: "1px solid #ced4da",
                                borderRadius: "4px",
                                marginTop: "2px",
                                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                            }}
                        >
                            <div style={{ padding: "8px" }}>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    autoFocus
                                    value={search}
                                    placeholder={props.searchPlaceholder ?? ""}
                                    onChange={(event) => setSearch(event.target.value)}
                                    onClick={(event) => event.stopPropagation()}
                                />
                            </div>
                            <div
                                style={{
                                    maxHeight: "220px",
                                    overflowY: "auto",
                                    padding: "0 8px 8px 8px",
                                }}
                            >
                                {filteredRows.map((row: any) => {
                                    const valueAsString = String(row[props.dataValueField]);
                                    const isChecked = props.value.indexOf(valueAsString) > -1;
                                    return (
                                        <div key={valueAsString} className="form-check">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id={`${props.id}_${valueAsString}`}
                                                checked={isChecked}
                                                onChange={(event) =>
                                                    toggleValue(valueAsString, event.target.checked)
                                                }
                                            />
                                            <label
                                                className="form-check-label"
                                                htmlFor={`${props.id}_${valueAsString}`}
                                                style={{ cursor: "pointer" }}
                                            >
                                                {row[props.dataTextField]}
                                            </label>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MultiSelectDropdown;
